import * as React from 'react';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import Image from 'next/image';
import { BuildingOfficeIcon, MapPinIcon, UsersIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Cog8ToothIcon, EyeIcon, EyeSlashIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import {
  cn,
  customImageLoader,
  domainInterestOptions,
  EmployerJPState,
  getEmployerJPState,
} from '@restworld/utils-common';
import { Badge, IBadgeProps } from '@restworld/ui-ds-base';
import { IconButton, Dropdown, Heading, Text } from '@restworld/ui-ds-base';

import { ImageSlider } from '../image-slider';
import { V2Link } from '../../feature-components';

export function getJPOnboardingFormLink({
  jpId,
  titleDescriptionSet,
  jpState,
}: {
  jpId: string;
  titleDescriptionSet?: boolean;
  jpState?: EmployerJPState;
}): string {
  let baseLink = `/employer/job-positions/onboarding?job_position_id=${jpId}`;
  switch (jpState) {
    case 'pending_approval':
      baseLink += `&step=review`;
      break;
    case 'draft':
      if (titleDescriptionSet) {
        baseLink += `&step=review`;
      }
      break;
    case 'open':
      // Editing an online JP reuses the onboarding wizard in "edit-online" mode:
      // land straight on the review step so the employer can jump to any section.
      baseLink += `&step=review`;
      break;
    default:
      break;
  }

  return baseLink;
}

export function EmployerCard({
  children,
  href,
  onClick,
  twStyles,
}: PropsWithChildren<{ href: string; onClick?: () => void; twStyles?: string[] }>) {
  return (
    <CardLinkContainer href={href} onClick={onClick} $twStyles={twStyles} prefetch>
      {children}
    </CardLinkContainer>
  );
}
export default EmployerCard;

export function EmployerCardHeader({
  logo,
  title,
  badge,
  statusKey,
  jpId,
  occupationLabel,
  isUsingPPU = false,
  onRequestChanges,
  onDuplicate,
  onDeleteDraft,
  onPutOffline,
  onPutOnline,
}: EmployerCardHeaderProps) {
  const { t } = useTranslation('job-positions-page');
  const router = useRouter();

  const jpState = useMemo(() => getEmployerJPState(statusKey), [statusKey]);

  const allMenuItems = useMemo(
    () => [
      {
        id: 'edit',
        label: t('jp_menu_edit'),
        Icon: PencilSquareIcon,
        onClick: () => {
          // Since right now, there is a 'success' step as the last one, so hardcoding 'review' here otherwise user will be redirected to 'success' step
          jpId && router.push(getJPOnboardingFormLink({ jpId, titleDescriptionSet: !!title, jpState }));
        },
        visibleIn: ['draft', 'pending_approval'],
      },
      {
        id: 'put_online',
        label: t('jp_menu_put_online'),
        Icon: EyeIcon,
        onClick: () => {
          jpId && onPutOnline?.(jpId);
        },
        visibleIn: ['offline'],
      },
      {
        id: 'request_changes',
        label: t('jp_menu_request_changes'),
        Icon: PencilSquareIcon,
        onClick: () => {
          onRequestChanges?.();
        },
        visibleIn: ['open', 'offline'],
        hideForPPU: true,
      },
      {
        id: 'ppu_modify',
        label: t('jp_menu_edit'),
        Icon: PencilSquareIcon,
        onClick: () => {
          // Same path as the draft/pending_approval edit: land on the review step
          // of the onboarding wizard so the employer can jump to any section.
          jpId && router.push(getJPOnboardingFormLink({ jpId, titleDescriptionSet: !!title, jpState }));
        },
        visibleIn: ['open'],
        onlyForPPU: true,
      },
      {
        id: 'duplicate',
        label: t('jp_menu_duplicate'),
        Icon: DocumentDuplicateIcon,
        onClick: () => {
          jpId && onDuplicate?.(jpId);
        },
        visibleIn: ['closed', 'open', 'pending_approval', 'draft', 'offline'],
      },
      {
        id: 'delete_draft',
        label: t('jp_menu_delete_draft'),
        Icon: TrashIcon,
        onClick: () => {
          jpId && onDeleteDraft?.(jpId);
        },
        isCriticalAction: true,
        visibleIn: ['draft'],
      },
      {
        id: 'cancel_approval',
        label: t('jp_menu_cancel_approval'),
        Icon: TrashIcon,
        onClick: () => {
          jpId && onDeleteDraft?.(jpId);
        },
        isCriticalAction: true,
        visibleIn: ['pending_approval'],
      },
      {
        id: 'put_offline',
        label: t('jp_menu_put_offline'),
        Icon: EyeSlashIcon,
        onClick: () => {
          jpId && onPutOffline?.(jpId);
        },
        isCriticalAction: true,
        visibleIn: ['open'],
      },
    ],
    [t, router, jpId, jpState, onRequestChanges, onDuplicate, onDeleteDraft, onPutOffline, onPutOnline, title]
  );

  const menuItems = useMemo(
    () =>
      allMenuItems
        .filter((item) => item.visibleIn.includes(jpState))
        .filter((item) => {
          if (item.hideForPPU && isUsingPPU) return false;
          if (item.onlyForPPU && !isUsingPPU) return false;
          return true;
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ id, visibleIn, hideForPPU, onlyForPPU, ...item }) => item),
    [allMenuItems, jpState, isUsingPPU]
  );

  const logoElement = logo && (
    <LogoWrapper>
      <Image src={logo} alt="" fill loader={customImageLoader} className="object-cover object-center" />
    </LogoWrapper>
  );

  const titleElement = (
    <Heading as="span" size={4} weight="medium" className="max-w-full text-xl">
      {title || (jpState === 'draft' ? t('jp_draft_of_search', { role: occupationLabel }) : null)}
    </Heading>
  );

  const badgeElement = badge && (
    <BadgeWrapper>
      <Badge variant={badge.variant}>{badge.text}</Badge>
    </BadgeWrapper>
  );

  const menuElement = jpId && (
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Dropdown
        items={menuItems}
        triggerElement={<IconButton ariaLabel="more options" Icon={Cog8ToothIcon} />}
        disabled={menuItems.length === 0}
      />
    </div>
  );

  return (
    <CardHeaderWrapper>
      {/* Mobile Layout */}
      <LogoAndMenuWrapper>
        <div>{logoElement}</div>
        {menuElement && <div>{menuElement}</div>}
      </LogoAndMenuWrapper>
      <TitleMobileWrapper>{titleElement}</TitleMobileWrapper>
      <BadgeMobileWrapper>{badgeElement}</BadgeMobileWrapper>

      {/* Desktop Layout */}
      <LogoAndTitleWrapper>
        {logoElement}
        <TitleWrapper>{titleElement}</TitleWrapper>
      </LogoAndTitleWrapper>
      <HeaderActionsWrapper>
        {badgeElement}
        {menuElement}
      </HeaderActionsWrapper>
    </CardHeaderWrapper>
  );
}
export function EmployerCardImages({ images }: { images?: string[] }) {
  return (
    <div>
      <ImageSlider images={images} />
    </div>
  );
}

export function EmployerCardInfo({ info }: EmployerCardInfoProps) {
  const { t: translateCommom } = useTranslation('common');

  const DomainInterestIcon = useMemo(
    () => domainInterestOptions.find((option) => option.key === info.domainInterestKey)?.iconElement,
    [info.domainInterestKey]
  );

  return (
    <CardInfoWrapper>
      {InfoItems.map(({ key, icon }) => {
        const value = info[key];
        return value ? (
          <InfoItem
            key={key}
            icon={key === 'occupation' && DomainInterestIcon ? <DomainInterestIcon className="h-4 w-4" /> : icon}
            text={`${value} ${key === 'openPositions' ? translateCommom('people_wanted') : ''}`}
          />
        ) : null;
      })}
    </CardInfoWrapper>
  );
}

export function EmployerCardFooter({ labels, notification }: EmployerCardFooterProps) {
  const hasLabels = !!labels?.length;

  return (
    <CardFooterWrapper>
      {!!labels?.length && (
        <LabelsWrapper>
          {labels.map(({ text, variant = 'info' }) => (
            <Badge key={text} variant={variant}>
              {text}
            </Badge>
          ))}
        </LabelsWrapper>
      )}
      {!!notification?.candidateCount && notification?.message && notification.candidates && (
        <NotificationSectionWrapper hasLabels={hasLabels}>
          <NewCandidateNotification
            candidates={notification.candidates}
            message={notification.message}
            candidateCount={notification.candidateCount}
            isAllSpontaneous={notification.isAllSpontaneous}
          />
        </NotificationSectionWrapper>
      )}
    </CardFooterWrapper>
  );
}

function InfoItem({ icon, text }: PropsWithChildren<{ icon: ReactNode; text: string | number }>) {
  return (
    <InfoItemWrapper>
      <IconWrapper>{icon}</IconWrapper>
      <Text as="span" size={2} weight="medium" color="muted">
        {text}
      </Text>
    </InfoItemWrapper>
  );
}

export function NewCandidateNotification({
  isAllSpontaneous,
  message,
  wrapperStyle,
  imageWrapperStyle,
  candidateCount = 0,
  candidates = [],
  imageDisplayLimit = 3,
  leftOffset = 16,
}: NewCandidateNotificationProps) {
  const moreToShowCount = candidateCount && candidateCount - imageDisplayLimit;
  const hasMoreToShow = !!moreToShowCount && moreToShowCount > 0;
  const textLeftOffset =
    (Math.min(candidateCount, imageDisplayLimit) - (candidateCount === 2 || candidateCount === 3 ? 1 : 0)) *
      leftOffset -
    0;

  return candidates?.length ? (
    <NotificationWrapper isSpontaneous={isAllSpontaneous} twStyle={wrapperStyle}>
      <div className="flex">
        {candidates.slice(0, imageDisplayLimit).map((candidate, i) => {
          return (
            <RoundedWrapper
              key={i}
              isPositioned={i > 0}
              twStyles={[{ left: i === 1 ? 18 : i * leftOffset }, imageWrapperStyle || '']}
            >
              {candidate.image ? (
                <Image
                  src={candidate.image}
                  alt=""
                  width={28}
                  height={28}
                  loader={customImageLoader}
                  className="object-cover object-center rounded-full h-7 w-7"
                />
              ) : (
                <Heading
                  as="span"
                  size={5}
                  weight="semibold"
                  className="h-7 w-7 rounded-full grid place-items-center text-sm uppercase bg-purple-50 text-purple-700"
                >
                  {`${candidate?.firstName ? candidate.firstName[0] : ''}${
                    candidate?.lastName ? candidate.lastName[0] : ''
                  }`}
                </Heading>
              )}
            </RoundedWrapper>
          );
        })}
        {!!hasMoreToShow && (
          <RoundedWrapper isPositioned twStyles={[{ left: imageDisplayLimit * leftOffset }, imageWrapperStyle || '']}>
            <Heading
              as="span"
              size={5}
              weight="semibold"
              className="h-7 w-7 rounded-full grid place-items-center text-sm bg-primary-50 text-primary-700"
            >
              {`+${moreToShowCount}`}
            </Heading>
          </RoundedWrapper>
        )}
      </div>
      {!!message && (
        <Text
          as="span"
          size={2}
          weight="medium"
          color="white"
          style={{ marginLeft: candidateCount > 1 ? textLeftOffset : 0 }}
        >
          {message}
        </Text>
      )}
    </NotificationWrapper>
  ) : null;
}

interface EmployerCardHeaderProps {
  logo?: string;
  title?: string;
  badge?: EmployerBadge;
  statusKey?: string;
  jpId?: string;
  occupationLabel?: string;
  isUsingPPU?: boolean;
  onRequestChanges?: () => void;
  onDuplicate?: (jpId: string) => void;
  onDeleteDraft?: (jpId: string) => void;
  onPutOffline?: (jpId: string) => void;
  onPutOnline?: (jpId: string) => void;
}
interface EmployerCardInfoProps {
  info: {
    address?: string;
    businessName?: string;
    occupation?: string;
    openPositions?: number;
    domainInterestKey?: string;
  };
}
export interface EmployerBadge extends Pick<IBadgeProps, 'variant'> {
  text: string;
}
interface EmployerCardFooterProps {
  labels?: EmployerBadge[];
  notification?: {
    candidateCount?: number;
    candidates?: EmployerCandidate[];
    message?: string;
    isAllSpontaneous?: boolean;
  };
}

export type EmployerCandidate = { firstName: string; lastName: string; image?: string };
export interface NewCandidateNotificationProps {
  message?: string;
  isAllSpontaneous?: boolean;
  candidateCount?: number;
  imageDisplayLimit?: number;
  leftOffset?: number;
  candidates?: EmployerCandidate[];
  wrapperStyle?: string;
  imageWrapperStyle?: string;
}

const InfoIconMap = {
  address: <MapPinIcon className="h-4 w-4" />,
  businessName: <BuildingOfficeIcon className="h-4 w-4" />,
  occupation: <BuildingOfficeIcon className="h-4 w-4" />,
  openPositions: <UsersIcon className="h-4 w-4" />,
};
const InfoItems: { key: keyof typeof InfoIconMap; icon: ReactNode }[] = [
  { key: 'businessName', icon: InfoIconMap.businessName },
  { key: 'occupation', icon: InfoIconMap.occupation },
  { key: 'address', icon: InfoIconMap.address },
  { key: 'openPositions', icon: InfoIconMap.openPositions },
];

const CardLinkContainer = ({ $twStyles, className, ...props }: any) => (
  <V2Link
    className={cn(
      // h-full: the card is a grid item; without it each card sizes to its own content and cards in
      // the same row end up different heights. Filling the cell keeps a row's cards equal height.
      "h-full p-5 border border-neutral-40 rounded-2xl bg-neutral-10 flex flex-col gap-4 box-border transition-all shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] hover:border-purple-500 hover:shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05,inset_0_0_0_1px_theme('colors.purple.500'))]",
      $twStyles,
      className
    )}
    {...props}
  />
);
const CardHeaderWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex flex-col gap-2 max-w-full md:flex-row md:items-center md:justify-between', className)}
    {...props}
  />
);
const LogoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border border-neutral-40 rounded-lg relative w-8 h-8 min-w-[2rem] flex items-center justify-center object-cover object-center overflow-hidden',
      className
    )}
    {...props}
  />
);
const LogoAndMenuWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-between gap-2 w-full md:hidden', className)} {...props} />
);
const TitleMobileWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('max-w-full md:hidden', className)} {...props} />
);
const BadgeMobileWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('md:hidden', className)} {...props} />
);
const LogoAndTitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'hidden md:flex md:items-center md:gap-2 md:max-w-[75%] md:w-[75%] sm:w-[100%] 2xl:max-w-[62%]',
      className
    )}
    {...props}
  />
);
const TitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('max-w-full', className)} {...props} />
);
const HeaderActionsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('hidden md:flex md:items-center md:gap-2', className)} {...props} />
);
const BadgeWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('whitespace-nowrap min-w-fit max-w-fit lowercase', className)} {...props} />
);
const CardInfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-2 flex-wrap', className)} {...props} />
);
const CardFooterWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col  gap-4 md:flex-row md:items-center', className)} {...props} />
);
const NotificationSectionWrapper = ({ hasLabels, className, ...props }: any) => (
  <div
    className={cn('flex-1 rounded-full flex', hasLabels ? 'md:justify-end' : 'md:justify-start', className)}
    {...props}
  />
);
const LabelsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-2 flex-1 flex-wrap lowercase', className)} {...props} />
);
const InfoItemWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-1 w-fit flex-nowrap', className)} {...props} />
);
const IconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-4 h-4', className)} {...props} />
);
const RoundedWrapper = ({ twStyles, className, ...props }: any) => (
  <div
    className={cn(
      'h-8 w-8 rounded-full bg-white grid place-items-center',
      ({ isPositioned }) => isPositioned && 'absolute',
      twStyles || '',
      className
    )}
    {...props}
  />
);
const NotificationWrapper = ({ isSpontaneous, twStyle, className, ...props }: any) => (
  <div
    className={cn(
      'bg-purple-500 rounded-full p-1 flex items-center gap-2 w-fit pr-3 min-h-[40px] truncate relative',
      twStyle || '',
      twStyle,
      className
    )}
    {...props}
  />
);
